import React, { useMemo, useState } from 'react';
import { CalendarDays, Plus, X, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import MaterialRow, { ROW_GRID } from './MaterialRow';
import SetRow from './SetRow';
import CityBusPlanGrid from './CityBusPlanGrid';
import HitachiPlanGrid from './HitachiPlanGrid';
import { fmtDate } from './helpers';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DayDetailPanel({ date, plans, projects, components, mdpData, periodMdp, lines, mappings, onClose, onUpdate, onDelete, onReschedule, onQuickEdit, onRemoveProject, onAddPlan, onRefreshPlans, isAdmin = true }) {
  const [changingLine, setChangingLine] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [collapsedProjects, setCollapsedProjects] = useState({});

  const toggleCat = (cat) => setCollapsedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  const toggleProject = (pid) => setCollapsedProjects(prev => ({ ...prev, [pid]: !prev[pid] }));

  const byProject = useMemo(() => {
    const map = {};
    plans.forEach(p => {
      const key = p.project_id;
      if (!map[key]) map[key] = { project: null, plans: [], lineId: p.line_id };
      map[key].plans.push(p);
    });
    Object.keys(map).forEach(pid => {
      const proj = projects.find(pr => pr.id === pid);
      map[pid].project = proj || { name: '?', category: '?', planning_unit: 'unit' };
    });
    return map;
  }, [plans, projects]);

  const getLineName = (lineId) => {
    const line = lines?.find(l => l.id === lineId);
    return line ? line.name : '';
  };

  const handleChangeLine = async (projectId, currentDate, newLineId) => {
    try {
      const projectPlans = plans.filter(p => p.project_id === projectId && p.date === currentDate);
      for (const p of projectPlans) {
        await axios.put(`${API}/api/plans/${p.id}`, { line_id: newLineId });
      }
      toast.success(`Moved to ${getLineName(newLineId)}`);
      setChangingLine(null);
      if (onRefreshPlans) onRefreshPlans();
    } catch (err) {
      toast.error('Failed to change line');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="day-detail-panel">
      {/* Header - fixed at top */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{fmtDate(date)}</h3>
          <p className="text-[11px] text-slate-500">{plans.length} plan entries</p>
        </div>
        <div className="flex items-center gap-2">
          {onAddPlan && (
            <button onClick={() => onAddPlan(date)}
              className="flex items-center gap-1 px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="day-panel-add-plan">
              <Plus className="w-3.5 h-3.5" /> New Plan
            </button>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
        {(() => {
          // Group projects by category
          const catGroups = {};
          Object.entries(byProject).forEach(([pid, data]) => {
            const cat = data.project?.category || data.project?.category_name || (data.project?.planning_unit === 'seat' ? 'Train' : 'Coach');
            if (!catGroups[cat]) catGroups[cat] = [];
            catGroups[cat].push({ pid, ...data });
          });

          const catOrder = ['Train', 'Coach', 'City Bus'];
          const sortedCats = Object.keys(catGroups).sort((a, b) => {
            const ai = catOrder.indexOf(a), bi = catOrder.indexOf(b);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          });

          return sortedCats.map(cat => {
            const isCollapsed = collapsedCats[cat];
            const catProjects = catGroups[cat];
            const totalPlans = catProjects.reduce((s, p) => s + p.plans.length, 0);

            const catStyles = {
              Train: { bg: 'bg-blue-50 hover:bg-blue-100 border-blue-200', icon: 'text-blue-500', label: 'text-blue-700', sub: 'text-blue-400' },
              Coach: { bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200', icon: 'text-orange-500', label: 'text-orange-700', sub: 'text-orange-400' },
              'City Bus': { bg: 'bg-green-50 hover:bg-green-100 border-green-200', icon: 'text-green-500', label: 'text-green-700', sub: 'text-green-400' },
            };
            const style = catStyles[cat] || catStyles.Coach;

            return (
              <div key={cat} data-testid={`day-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                <button onClick={() => toggleCat(cat)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg mb-1 transition-colors border ${style.bg}`}
                  data-testid={`day-cat-toggle-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-2">
                    {isCollapsed ? <ChevronRight className={`w-3.5 h-3.5 ${style.icon}`} /> : <ChevronDown className={`w-3.5 h-3.5 ${style.icon}`} />}
                    <span className={`text-xs font-bold ${style.label}`}>{cat}</span>
                    <span className={`text-[10px] ${style.sub}`}>{catProjects.length} project{catProjects.length !== 1 ? 's' : ''} · {totalPlans} entries</span>
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="space-y-3">
                    {catProjects.map(({ pid, project, plans: pPlans, lineId }) => {
          const isTrain = project.planning_unit === 'seat' || pPlans[0]?.planning_unit === 'seat';
          const isSet = project.planning_unit === 'set' || pPlans[0]?.planning_unit === 'set';
          const isHitachi = !!(project.hitachi_seat_types?.length > 0 || pPlans[0]?.is_hitachi);
          const projComponents = components[pid] || [];
          const seatComponents = projComponents.filter(c => c.is_seat);
          const otherComponents = projComponents.filter(c => !c.is_seat && c.material_description);
          const planByMaterial = {};
          pPlans.forEach(p => { if (p.material_description) planByMaterial[p.material_description] = p; });
          const projMdp = periodMdp?.[pid]?.materials || mdpData?.[pid] || null;
          const projMappings = mappings?.[pid] || {};
          const ksuToTc = projMappings._ksuToTc || {};
          const lineName = getLineName(lineId);

          // Create plan for a component that doesn't have one yet
          const handleCreatePlanForComponent = async (comp) => {
            try {
              const res = await axios.post(`${API}/api/plans/create-single`, {
                date, project_id: pid, line_id: lineId,
                material_code: comp.material_code,
                material_description: comp.material_description,
                seats_per_unit: comp.seats_per_unit || 0,
                is_seat: comp.is_seat || false,
              });
              if (res.data?.id) {
                toast.success(`${comp.material_description} added to plan`);
                onRefreshPlans();
              }
            } catch (err) {
              toast.error('Failed to create plan');
            }
          };

          const seatPlans = isTrain ? pPlans.filter(p => (p.seats_per_unit || 0) > 0) : [];
          const totalSeatPlaced = seatPlans.reduce((s, p) => s + (p.planned_quantity || 0) * (p.seats_per_unit || 0), 0);
          const completedSeatPlaced = seatPlans.reduce((s, p) => s + (p.completed_quantity || 0) * (p.seats_per_unit || 0), 0);
          const totalSets = isSet ? pPlans.reduce((s, p) => s + (p.planned_quantity || 0), 0) : 0;
          const ups = project.units_per_set || 1;

          return (
            <div key={pid} className="border border-slate-200 rounded-lg" data-testid={`day-project-${pid}`}>
              {/* Sticky block: Project Header + Column Headers */}
              <div className="sticky top-0 z-10">
                {/* Project Header - CLICKABLE TO COLLAPSE */}
                <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between rounded-t-lg cursor-pointer select-none"
                  onClick={() => toggleProject(pid)} data-testid={`toggle-project-${pid}`}>
                  <div className="flex items-center gap-2">
                    {collapsedProjects[pid]
                      ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                      : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    }
                    <span className="text-sm font-bold text-slate-800">{project.name}</span>
                    {project.sr_number && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-600 border border-blue-200">{project.sr_number}</span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isTrain ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-600'}`}>
                      {project.category || project.category_name}
                    </span>
                    {project.seat_type_name && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-violet-50 text-violet-600 border border-violet-200">{project.seat_type_name}</span>
                    )}
                    {isSet && project.has_nemo && project.nemo_seat_type && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-teal-50 text-teal-600 border border-teal-200">{project.nemo_seat_type}</span>
                    )}
                    {/* Carousel Line dropdown */}
                    {isAdmin && (
                    <div className="relative" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setChangingLine(changingLine === pid ? null : pid)}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium hover:bg-slate-300 flex items-center gap-0.5 cursor-pointer"
                        data-testid={`change-line-btn-${pid}`}>
                        {lineName || 'Line'}
                        <ChevronDown className="w-2.5 h-2.5" />
                      </button>
                      {changingLine === pid && lines && (
                        <div className="absolute top-full left-0 mt-1 z-30 bg-white rounded-lg shadow-xl border border-slate-200 py-1 w-48" data-testid={`line-dropdown-${pid}`}>
                          {lines.map(l => (
                            <button key={l.id} onClick={() => handleChangeLine(pid, date, l.id)}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-2 ${l.id === lineId ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700'}`}
                              data-testid={`line-option-${l.id}`}>
                              <span className="w-5 h-5 rounded bg-slate-700 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">{l.code}</span>
                              <span className="truncate">{l.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                    {!isAdmin && lineName && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">{lineName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isTrain && !isHitachi && <span className="text-xs font-semibold text-blue-700">{completedSeatPlaced}/{totalSeatPlaced} <span className="text-blue-400 text-[10px]">sp</span></span>}
                    {isHitachi && <span className="text-xs font-semibold text-indigo-700">{pPlans.filter(p => p.completed_quantity > 0).length}/{pPlans.length} <span className="text-indigo-400 text-[10px]">sets</span></span>}
                    {isSet && <span className="text-xs font-semibold text-orange-700">{pPlans.reduce((s, p) => s + (p.completed_quantity || 0), 0)}/{totalSets} <span className="text-orange-400 text-[10px]">sets</span></span>}
                    {isAdmin && onRemoveProject && (
                      <button onClick={(e) => { e.stopPropagation(); onRemoveProject(pid, date); }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`remove-project-${pid}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Column headers - directly below project header, no gap */}
                {!collapsedProjects[pid] && isTrain && !isHitachi && (
                  <div className={`${ROW_GRID} px-4 py-1.5 bg-slate-100 border-b border-slate-200`}>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Component</span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase text-center border-r border-slate-200">MDP<span className="block text-[7px] font-normal text-amber-400 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase text-center border-r border-slate-200">TOT<span className="block text-[7px] font-normal text-slate-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase text-center border-r border-slate-200">PLN<span className="block text-[7px] font-normal text-slate-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase text-center border-r border-slate-200">DONE<span className="block text-[7px] font-normal text-slate-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-green-600 uppercase text-center border-r border-slate-200">C.PRD<span className="block text-[7px] font-normal text-green-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-blue-600 uppercase text-center border-r border-slate-200">C.SHP<span className="block text-[7px] font-normal text-blue-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-red-500 uppercase text-center border-r border-slate-200">S.PRD<span className="block text-[7px] font-normal text-red-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-orange-500 uppercase text-center border-r border-slate-200">S.SHP<span className="block text-[7px] font-normal text-orange-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-purple-600 uppercase text-center border-r border-slate-200">S.PLN<span className="block text-[7px] font-normal text-purple-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-orange-600 uppercase text-center border-r border-slate-200">REM<span className="block text-[7px] font-normal text-orange-300 normal-case">seat</span></span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase text-center">TOTAL<span className="block text-[7px] font-normal text-slate-300 normal-case">seat</span></span>
                    <span></span>
                  </div>
                )}
              </div>

              {!collapsedProjects[pid] && isTrain && !isHitachi && (
                <div>
                  {seatComponents.length > 0 && (
                    <div>
                      <div className="px-4 py-1 bg-blue-50/50 border-b border-blue-100">
                        <span className="text-[10px] font-bold text-blue-600 uppercase">Seats ({seatComponents.length})</span>
                      </div>
                      {seatComponents.map(comp => (
                        <MaterialRow key={comp.id} plan={planByMaterial[comp.material_description] || null}
                          component={comp} showSeatPlaced
                          mdpInfo={projMdp?.[comp.material_description] || projMdp?.[comp.material_code] || null}
                          mappingCodes={projMappings[comp.material_description] || null}
                          ksuToTc={ksuToTc}
                          onUpdate={onUpdate} onDelete={onDelete} onReschedule={onReschedule} onQuickEdit={onQuickEdit} isAdmin={isAdmin}
                          onCreatePlan={handleCreatePlanForComponent} />
                      ))}
                      {/* Seat totals row */}
                      {(() => {
                        let tPln = 0, tDone = 0;
                        seatComponents.forEach(comp => {
                          const pl = planByMaterial[comp.material_description];
                          tPln += pl?.planned_quantity || 0;
                          tDone += pl?.completed_quantity || 0;
                        });
                        return (
                          <div className={`${ROW_GRID} px-4 py-1.5 bg-blue-100/70 border-t-2 border-blue-300`} data-testid="seat-totals-row">
                            <span className="text-[11px] font-extrabold text-blue-800 uppercase tracking-wider">Total Seats</span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm font-extrabold text-center text-blue-700 border-r border-blue-200" data-testid="seat-total-pln">{tPln}</span>
                            <span className="text-sm font-extrabold text-center text-blue-700 border-r border-blue-200" data-testid="seat-total-done">{tDone}</span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center border-r border-blue-200"></span>
                            <span className="text-sm text-center"></span>
                            <span></span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {otherComponents.length > 0 && (
                    <div>
                      <div className="px-4 py-1 bg-slate-50 border-b border-slate-100">
                        <span className="text-[10px] font-bold text-slate-500 uppercase">Others ({otherComponents.length})</span>
                      </div>
                      {otherComponents.map(comp => (
                        <MaterialRow key={comp.id} plan={planByMaterial[comp.material_description] || null}
                          component={comp} showSeatPlaced={false}
                          mdpInfo={projMdp?.[comp.material_description] || projMdp?.[comp.material_code] || null}
                          mappingCodes={projMappings[comp.material_description] || null}
                          ksuToTc={ksuToTc}
                          onUpdate={onUpdate} onDelete={onDelete} onReschedule={onReschedule} onQuickEdit={onQuickEdit} isAdmin={isAdmin}
                          onCreatePlan={handleCreatePlanForComponent} />
                      ))}
                    </div>
                  )}
                  {pPlans.filter(p => p.material_description && !projComponents.find(c => c.material_description === p.material_description)).length > 0 && (
                    <div>
                      <div className="px-4 py-1 bg-amber-50/50 border-b border-amber-100">
                        <span className="text-[10px] font-bold text-amber-600 uppercase">Unmatched</span>
                      </div>
                      {pPlans.filter(p => p.material_description && !projComponents.find(c => c.material_description === p.material_description)).map(p => (
                        <MaterialRow key={p.id} plan={p} component={null} showSeatPlaced={false} mdpInfo={projMdp?.[p.material_description] || null}
                          mappingCodes={projMappings[p.material_description] || null}
                          ksuToTc={ksuToTc}
                          onUpdate={onUpdate} onDelete={onDelete} onReschedule={onReschedule} onQuickEdit={onQuickEdit} isAdmin={isAdmin} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!collapsedProjects[pid] && isHitachi && (
                <HitachiPlanGrid
                  plans={pPlans}
                  projectId={pid}
                  project={project}
                  planDate={date}
                  onRefresh={onRefreshPlans}
                  onDelete={onDelete}
                  isAdmin={isAdmin}
                />
              )}

              {!collapsedProjects[pid] && isSet && (
                <CityBusPlanGrid
                  plans={pPlans}
                  projectId={pid}
                  project={project}
                  planDate={date}
                  onRefresh={onRefreshPlans}
                  onDelete={onDelete}
                  isAdmin={isAdmin}
                />
              )}
            </div>
          );
                    })}
                  </div>
                )}
              </div>
            );
          });
        })()}

        {Object.keys(byProject).length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No plans for this day</p>
          </div>
        )}
      </div>
    </div>
  );
}
